import { StorageContextContent } from "p6m-contexts";
import type { StudentTestAnswer, StudentTestContent } from "p6m-p6u";
import axios from "axios";
import { createContext, useCallback, useContext, useState } from "react";
import { getRequest } from "../helpers/networkHelper";
import { getIsLocalTestRun, getIsTestRun } from "../helpers/Url";
import { useTestCreationContext } from "./TestCreationContext";
import { Views } from "../enums/views";
import { NetworkErrorCodes } from "../networking/errorCodes";
import { useHistory } from "react-router-dom";

/** What `refreshData` answers. NOT_FOUND is terminal; FAILED is worth retrying. */
export enum StudentTestLoadOutcomes {
    LOADED = "LOADED",
    NOT_FOUND = "NOT_FOUND",
    FAILED = "FAILED",
}

const StudentPracticeContext = createContext<StorageContextContent | undefined>(undefined);

const useStudentPracticeContext = () => {
    const context = useContext(StudentPracticeContext);

    if (!context) {
        throw new Error("useStudentPracticeContext must be used inside the provider");
    }

    const {
        studentTestAnswer,
        setStudentTestAnswer,
        teacherTest,
        setTeacherTest,
        currentMainView,
        setCurrentMainView,
        checkCurrentViewsRestart,
        isPreview,
        setIsPreview,
        isOwner,
        setIsOwner,
    } = context;

    const { createPreviewData } = useTestCreationContext();
    const history = useHistory();

    const updateTeacherTest = useCallback(
        (newTest: StudentTestContent, isPreview?: boolean) => {
            setTeacherTest(newTest);
            checkCurrentViewsRestart(isPreview);
        },
        [setTeacherTest, checkCurrentViewsRestart]
    );

    /**
     * Loads the shared test into the context and reports what happened, so the caller can tell "this test
     * is gone" from "the network dropped" and offer a retry only for the second.
     */
    const refreshData = useCallback(
        async (testId: string): Promise<StudentTestLoadOutcomes> => {
            try {
                const studentTestData = await getRequest(`aea/student-tests/?testId=${testId}`);
                if (!studentTestData.data) return StudentTestLoadOutcomes.NOT_FOUND;

                const retrievedTestData: StudentTestContent = { ...studentTestData.data, id: testId };
                updateTeacherTest(retrievedTestData);
                return StudentTestLoadOutcomes.LOADED;
            } catch (e) {
                const isNotFound = axios.isAxiosError(e) && e.response?.status === NetworkErrorCodes.NOT_FOUND;
                if (!isNotFound) console.error("Failed to load the shared test", e);
                return isNotFound ? StudentTestLoadOutcomes.NOT_FOUND : StudentTestLoadOutcomes.FAILED;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const refreshPreviewData = () => {
        const testData = createPreviewData();

        if (testData && testData.hasOwnProperty("id") && testData.hasOwnProperty("name")) {
            setIsPreview(true);
            updateTeacherTest(testData, true);
        } else {
            history.push("/create");
        }
    };

    return {
        refreshData,
        refreshPreviewData,
        updateTeacherTest,
        studentTestAnswer,
        setStudentTestAnswer,
        teacherTest,
        currentMainView,
        setCurrentMainView,
        isPreview,
        setIsPreview,
        isOwner,
        setIsOwner,
    };
};

const StudentPracticeProvider = (props: React.PropsWithChildren<{}>) => {
    const [studentTestAnswer, setStudentTestAnswer] = useState<StudentTestAnswer>({ questionAnswers: {} });
    const [teacherTest, setTeacherTest] = useState<StudentTestContent>({});
    const [currentMainView, setCurrentMainView] = useState<Views | number>(-1);
    const [isPreview, setIsPreview] = useState(false);
    const [isOwner, setIsOwner] = useState(false);

    const checkCurrentViewsRestart = useCallback(
        (isPreview?: boolean) => {
            const noActiveView = currentMainView < 0;
            const isAfterPractice = currentMainView > Views.EXERCISES;

            if (noActiveView || isAfterPractice) {
                const isTeacherPreview = isPreview || getIsTestRun() || getIsLocalTestRun();
                setCurrentMainView(isTeacherPreview ? Views.EXERCISES : Views.INTRO);
            }
        },
        [currentMainView]
    );

    const value = {
        studentTestAnswer,
        setStudentTestAnswer,
        teacherTest,
        setTeacherTest,
        currentMainView,
        setCurrentMainView,
        checkCurrentViewsRestart,
        isPreview,
        setIsPreview,
        isOwner,
        setIsOwner,
    };

    return <StudentPracticeContext.Provider value={value}>{props.children}</StudentPracticeContext.Provider>;
};

export { StudentPracticeProvider, useStudentPracticeContext };
